import React, { useState } from "react";
import SortableList, { DragHandle } from "../../ui/SortableList";
import TaskBuilderModal from "../modals/TaskBuilderModal";
import Button from "../../ui/Button";

/**
 * Reusable Task list editor
 * Manages array of tasks with drag-and-drop reordering
 * Opens TaskBuilderModal for editing individual tasks
 */
const TaskListEditor = ({
  tasks = [],
  onChange,
  mode = "project",
  showSaveToRegistry = false,
  onAddFromRegistry,
  disabled = false
}) => {
  const [editingTask, setEditingTask] = useState(null);
  const [showTaskModal, setShowTaskModal] = useState(false);

  const handleAddNew = () => {
    setEditingTask(null);
    setShowTaskModal(true);
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    setShowTaskModal(true);
  };

  const handleSaveTask = (taskData) => {
    if (editingTask) {
      // Update existing task
      onChange(tasks.map(t => 
        (t.id || t._id) === (editingTask.id || editingTask._id) 
          ? { ...editingTask, ...taskData }
          : t
      ));
    } else {
      // Add new task
      const newTask = {
        ...taskData,
        id: `temp-${Date.now()}`,
        isNew: true,
        sortOrder: tasks.length
      };
      onChange([...tasks, newTask]);
    }
  };

  const handleRemove = (taskId) => {
    if (window.confirm("Remove this task and all its subtasks?")) {
      onChange(tasks.filter(t => (t.id || t._id) !== taskId));
    }
  };

  const handleReorder = (reorderedItems) => {
    const reordered = reorderedItems.map((item, index) => ({
      ...item.task,
      sortOrder: index
    }));
    onChange(reordered);
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <label className="block text-sm font-medium">
          Tasks ({tasks.length})
        </label>
        <div className="flex gap-2">
          {onAddFromRegistry && (
            <button
              type="button"
              onClick={onAddFromRegistry}
              className="text-sm text-blue-600 hover:underline"
              disabled={disabled}
            >
              + From Registry
            </button>
          )}
          <button
            type="button"
            onClick={handleAddNew}
            className="text-sm text-blue-600 hover:underline"
            disabled={disabled}
          >
            + New Task
          </button>
        </div>
      </div>

      {tasks.length === 0 ? (
        <div className="border rounded p-6 text-center text-sm text-gray-500">
          No tasks yet. Add one to get started.
        </div>
      ) : (
        <SortableList
          items={tasks.map((task) => ({
            id: task.id || task._id,
            task
          }))}
          onReorder={handleReorder}
          renderItem={(item, dragHandleProps) => {
            const task = item.task;
            const subTaskCount = task.subTasks?.length || 0;

            return (
              <div className="bg-white border rounded p-3">
                <div className="flex items-center gap-2">
                  <DragHandle listeners={dragHandleProps} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">
                      {task.name || "(Unnamed)"}
                    </div>
                    {task.description && (
                      <div className="text-xs text-gray-500 truncate">
                        {task.description}
                      </div>
                    )}
                    <div className="text-xs text-gray-400 mt-1">
                      {subTaskCount} subtask{subTaskCount !== 1 ? 's' : ''}
                      {task.weight && ` • Weight: ${task.weight}`}
                      {task.estimatedHours && ` • ${task.estimatedHours}h`}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleEdit(task)}
                    className="text-sm text-blue-600 hover:underline px-2"
                    disabled={disabled}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRemove(item.id)}
                    className="text-red-600 hover:text-red-800 px-2"
                    disabled={disabled}
                    title="Remove task"
                  >
                    ✕
                  </button>
                </div>
              </div>
            );
          }}
        />
      )}

      {/* Task Builder Modal (nested modal) */}
      <TaskBuilderModal
        isOpen={showTaskModal}
        onClose={() => {
          setShowTaskModal(false);
          setEditingTask(null);
        }}
        onSave={handleSaveTask}
        task={editingTask}
        mode={mode}
        showSaveToRegistry={showSaveToRegistry}
      />
    </div>
  );
};

export default TaskListEditor;
